import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import {
  FiImage,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiCheck,
  FiX,
  FiLayers,
  FiTv,
  FiClock,
  FiExternalLink,
  FiUploadCloud,
  FiCalendar,
  FiMove,
} from "react-icons/fi";
import api from "../../utils/axios";

// ── Types ────────────────────────────────────────────────────────────────────

interface PopupItem {
  id: number;
  title: string;
  description: string;
  image_url: string;
  button_text: string;
  button_url: string;
  trigger_type: "on_load" | "after_delay" | "once_per_session" | "every_time";
  display_delay: number;
  start_date: string | null;
  end_date: string | null;
  show_close_button: boolean;
  popup_size: "small" | "medium" | "large";
  is_active: boolean;
}

interface BannerItem {
  id: number;
  title: string;
  desktop_image_url: string;
  mobile_image_url: string;
  redirect_url: string;
  display_order: number;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
}

interface AdItem {
  id: number;
  title: string;
  description: string;
  image_url: string;
  button_text: string;
  button_url: string;
  position: "home_top" | "home_middle" | "home_bottom" | "game_page" | "deposit_page" | "withdrawal_page";
  priority: number;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
}

const API_BASE_URL = "https://api.aaveera365.in";

const getImageFullUrl = (url: string) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) {
    return url;
  }
  return `${API_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

const PromotionsManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"popups" | "banners" | "ads">("popups");

  // Data states
  const [popups, setPopups] = useState<PopupItem[]>([]);
  const [banners, setBanners] = useState<BannerItem[]>([]);
  const [ads, setAds] = useState<AdItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Filter states
  const [adPositionFilter, setAdPositionFilter] = useState<string>("all");

  // Modal / Form States
  const [isPopupModalOpen, setIsPopupModalOpen] = useState<boolean>(false);
  const [editingPopup, setEditingPopup] = useState<PopupItem | null>(null);
  const [popupForm, setPopupForm] = useState<{
    title: string;
    description: string;
    image_url: string;
    button_text: string;
    button_url: string;
    trigger_type: "on_load" | "after_delay" | "once_per_session" | "every_time";
    display_delay: number;
    start_date: string;
    end_date: string;
    show_close_button: boolean;
    popup_size: "small" | "medium" | "large";
    is_active: boolean;
    imageFile: File | null;
  }>({
    title: "",
    description: "",
    image_url: "",
    button_text: "",
    button_url: "",
    trigger_type: "on_load",
    display_delay: 0,
    start_date: "",
    end_date: "",
    show_close_button: true,
    popup_size: "medium",
    is_active: true,
    imageFile: null,
  });

  // Banner Modal
  const [isBannerModalOpen, setIsBannerModalOpen] = useState<boolean>(false);
  const [editingBanner, setEditingBanner] = useState<BannerItem | null>(null);
  const [bannerForm, setBannerForm] = useState<{
    title: string;
    desktop_image_url: string;
    mobile_image_url: string;
    redirect_url: string;
    display_order: number;
    start_date: string;
    end_date: string;
    is_active: boolean;
    desktopFile: File | null;
    mobileFile: File | null;
  }>({
    title: "",
    desktop_image_url: "",
    mobile_image_url: "",
    redirect_url: "",
    display_order: 0,
    start_date: "",
    end_date: "",
    is_active: true,
    desktopFile: null,
    mobileFile: null,
  });

  // Ad Modal
  const [isAdModalOpen, setIsAdModalOpen] = useState<boolean>(false);
  const [editingAd, setEditingAd] = useState<AdItem | null>(null);
  const [adForm, setAdForm] = useState<{
    title: string;
    description: string;
    image_url: string;
    button_text: string;
    button_url: string;
    position: "home_top" | "home_middle" | "home_bottom" | "game_page" | "deposit_page" | "withdrawal_page";
    priority: number;
    start_date: string;
    end_date: string;
    is_active: boolean;
    imageFile: File | null;
  }>({
    title: "",
    description: "",
    image_url: "",
    button_text: "",
    button_url: "",
    position: "home_middle",
    priority: 0,
    start_date: "",
    end_date: "",
    is_active: true,
    imageFile: null,
  });

  // Live Preview Modal
  const [previewItem, setPreviewItem] = useState<{
    type: "popup" | "banner" | "ad";
    data: any;
  } | null>(null);

  // ── Fetch Functions ────────────────────────────────────────────────────────
  const fetchPopups = async () => {
    try {
      const res = await api.get("/promotions/popups");
      if (res.data?.status) setPopups(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch popups", err);
    }
  };

  const fetchBanners = async () => {
    try {
      const res = await api.get("/promotions/banners");
      if (res.data?.status) setBanners(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch banners", err);
    }
  };

  const fetchAds = async () => {
    try {
      const res = await api.get("/promotions/ads");
      if (res.data?.status) setAds(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch ads", err);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchPopups(), fetchBanners(), fetchAds()]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // ── Popup Handlers ─────────────────────────────────────────────────────────
  const openCreatePopup = () => {
    setEditingPopup(null);
    setPopupForm({
      title: "",
      description: "",
      image_url: "",
      button_text: "",
      button_url: "",
      trigger_type: "on_load",
      display_delay: 0,
      start_date: "",
      end_date: "",
      show_close_button: true,
      popup_size: "medium",
      is_active: true,
      imageFile: null,
    });
    setIsPopupModalOpen(true);
  };

  const openEditPopup = (item: PopupItem) => {
    setEditingPopup(item);
    setPopupForm({
      title: item.title || "",
      description: item.description || "",
      image_url: item.image_url || "",
      button_text: item.button_text || "",
      button_url: item.button_url || "",
      trigger_type: item.trigger_type || "on_load",
      display_delay: item.display_delay || 0,
      start_date: item.start_date ? item.start_date.substring(0, 16) : "",
      end_date: item.end_date ? item.end_date.substring(0, 16) : "",
      show_close_button: item.show_close_button,
      popup_size: item.popup_size || "medium",
      is_active: item.is_active,
      imageFile: null,
    });
    setIsPopupModalOpen(true);
  };

  const handleSavePopup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!popupForm.image_url && !popupForm.imageFile) {
      toast.warning("Please upload or provide an image for the popup");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("title", popupForm.title);
      formData.append("description", popupForm.description);
      formData.append("button_text", popupForm.button_text);
      formData.append("button_url", popupForm.button_url);
      formData.append("trigger_type", popupForm.trigger_type);
      formData.append("display_delay", popupForm.display_delay.toString());
      if (popupForm.start_date) formData.append("start_date", popupForm.start_date);
      if (popupForm.end_date) formData.append("end_date", popupForm.end_date);
      formData.append("show_close_button", popupForm.show_close_button.toString());
      formData.append("popup_size", popupForm.popup_size);
      formData.append("is_active", popupForm.is_active.toString());

      if (popupForm.imageFile) {
        formData.append("image", popupForm.imageFile);
      } else {
        formData.append("image_url", popupForm.image_url);
      }

      if (editingPopup) {
        await api.put(`/promotions/popups/${editingPopup.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Popup updated successfully!");
      } else {
        await api.post("/promotions/popups", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Popup created successfully!");
      }

      setIsPopupModalOpen(false);
      fetchPopups();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to save popup");
    }
  };

  const handleTogglePopup = async (id: number) => {
    try {
      await api.patch(`/promotions/popups/${id}/toggle`);
      toast.success("Popup status updated");
      fetchPopups();
    } catch (err) {
      toast.error("Failed to toggle popup status");
    }
  };

  const handleDeletePopup = async (id: number) => {
    const confirm = await Swal.fire({
      title: "Delete Popup?",
      text: "Are you sure you want to delete this promotional popup?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it",
    });

    if (confirm.isConfirmed) {
      try {
        await api.delete(`/promotions/popups/${id}`);
        toast.success("Popup deleted successfully");
        fetchPopups();
      } catch (err) {
        toast.error("Failed to delete popup");
      }
    }
  };

  // ── Banner Handlers ────────────────────────────────────────────────────────
  const openCreateBanner = () => {
    setEditingBanner(null);
    setBannerForm({
      title: "",
      desktop_image_url: "",
      mobile_image_url: "",
      redirect_url: "",
      display_order: banners.length + 1,
      start_date: "",
      end_date: "",
      is_active: true,
      desktopFile: null,
      mobileFile: null,
    });
    setIsBannerModalOpen(true);
  };

  const openEditBanner = (item: BannerItem) => {
    setEditingBanner(item);
    setBannerForm({
      title: item.title || "",
      desktop_image_url: item.desktop_image_url || "",
      mobile_image_url: item.mobile_image_url || "",
      redirect_url: item.redirect_url || "",
      display_order: item.display_order || 0,
      start_date: item.start_date ? item.start_date.substring(0, 16) : "",
      end_date: item.end_date ? item.end_date.substring(0, 16) : "",
      is_active: item.is_active,
      desktopFile: null,
      mobileFile: null,
    });
    setIsBannerModalOpen(true);
  };

  const handleSaveBanner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bannerForm.desktop_image_url && !bannerForm.desktopFile) {
      toast.warning("Please upload a desktop banner image");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("title", bannerForm.title);
      formData.append("redirect_url", bannerForm.redirect_url);
      formData.append("display_order", bannerForm.display_order.toString());
      if (bannerForm.start_date) formData.append("start_date", bannerForm.start_date);
      if (bannerForm.end_date) formData.append("end_date", bannerForm.end_date);
      formData.append("is_active", bannerForm.is_active.toString());

      if (bannerForm.desktopFile) {
        formData.append("desktop_image", bannerForm.desktopFile);
      } else {
        formData.append("desktop_image_url", bannerForm.desktop_image_url);
      }

      if (bannerForm.mobileFile) {
        formData.append("mobile_image", bannerForm.mobileFile);
      } else if (bannerForm.mobile_image_url) {
        formData.append("mobile_image_url", bannerForm.mobile_image_url);
      }

      if (editingBanner) {
        await api.put(`/promotions/banners/${editingBanner.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Banner updated successfully!");
      } else {
        await api.post("/promotions/banners", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Banner created successfully!");
      }

      setIsBannerModalOpen(false);
      fetchBanners();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to save banner");
    }
  };

  const handleToggleBanner = async (id: number) => {
    try {
      await api.patch(`/promotions/banners/${id}/toggle`);
      toast.success("Banner status updated");
      fetchBanners();
    } catch (err) {
      toast.error("Failed to toggle banner status");
    }
  };

  const handleDeleteBanner = async (id: number) => {
    const confirm = await Swal.fire({
      title: "Delete Banner?",
      text: "Are you sure you want to delete this promotional banner?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it",
    });

    if (confirm.isConfirmed) {
      try {
        await api.delete(`/promotions/banners/${id}`);
        toast.success("Banner deleted successfully");
        fetchBanners();
      } catch (err) {
        toast.error("Failed to delete banner");
      }
    }
  };

  // ── Ad Handlers ────────────────────────────────────────────────────────────
  const openCreateAd = () => {
    setEditingAd(null);
    setAdForm({
      title: "",
      description: "",
      image_url: "",
      button_text: "",
      button_url: "",
      position: "home_middle",
      priority: 0,
      start_date: "",
      end_date: "",
      is_active: true,
      imageFile: null,
    });
    setIsAdModalOpen(true);
  };

  const openEditAd = (item: AdItem) => {
    setEditingAd(item);
    setAdForm({
      title: item.title || "",
      description: item.description || "",
      image_url: item.image_url || "",
      button_text: item.button_text || "",
      button_url: item.button_url || "",
      position: item.position || "home_middle",
      priority: item.priority || 0,
      start_date: item.start_date ? item.start_date.substring(0, 16) : "",
      end_date: item.end_date ? item.end_date.substring(0, 16) : "",
      is_active: item.is_active,
      imageFile: null,
    });
    setIsAdModalOpen(true);
  };

  const handleSaveAd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adForm.image_url && !adForm.imageFile) {
      toast.warning("Please upload or provide an ad banner image");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("title", adForm.title);
      formData.append("description", adForm.description);
      formData.append("button_text", adForm.button_text);
      formData.append("button_url", adForm.button_url);
      formData.append("position", adForm.position);
      formData.append("priority", adForm.priority.toString());
      if (adForm.start_date) formData.append("start_date", adForm.start_date);
      if (adForm.end_date) formData.append("end_date", adForm.end_date);
      formData.append("is_active", adForm.is_active.toString());

      if (adForm.imageFile) {
        formData.append("image", adForm.imageFile);
      } else {
        formData.append("image_url", adForm.image_url);
      }

      if (editingAd) {
        await api.put(`/promotions/ads/${editingAd.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Ad updated successfully!");
      } else {
        await api.post("/promotions/ads", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Ad created successfully!");
      }

      setIsAdModalOpen(false);
      fetchAds();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to save ad");
    }
  };

  const handleToggleAd = async (id: number) => {
    try {
      await api.patch(`/promotions/ads/${id}/toggle`);
      toast.success("Ad status updated");
      fetchAds();
    } catch (err) {
      toast.error("Failed to toggle ad status");
    }
  };

  const handleDeleteAd = async (id: number) => {
    const confirm = await Swal.fire({
      title: "Delete Ad?",
      text: "Are you sure you want to delete this custom advertisement?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it",
    });

    if (confirm.isConfirmed) {
      try {
        await api.delete(`/promotions/ads/${id}`);
        toast.success("Ad deleted successfully");
        fetchAds();
      } catch (err) {
        toast.error("Failed to delete ad");
      }
    }
  };

  const filteredAds = ads.filter((ad) => {
    if (adPositionFilter === "all") return true;
    return ad.position === adPositionFilter;
  });

  return (
    <div className="space-y-6 pb-12">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FiLayers className="text-emerald-600" />
            Popup & Ads Management
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Manage promotional popups, homepage banners, and custom advertisement placements across the user website.
          </p>
        </div>

        {/* Tab switcher buttons */}
        <div className="flex bg-gray-100 p-1 rounded-lg border border-gray-200 self-start sm:self-auto">
          <button
            onClick={() => setActiveTab("popups")}
            className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${
              activeTab === "popups"
                ? "bg-emerald-600 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <FiTv size={14} />
            Popups ({popups.length})
          </button>
          <button
            onClick={() => setActiveTab("banners")}
            className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${
              activeTab === "banners"
                ? "bg-emerald-600 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <FiImage size={14} />
            Banners ({banners.length})
          </button>
          <button
            onClick={() => setActiveTab("ads")}
            className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${
              activeTab === "ads"
                ? "bg-emerald-600 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <FiLayers size={14} />
            Ads Placements ({ads.length})
          </button>
        </div>
      </div>

      {/* ────────────────────────────────────────────────────────────────────────
          TAB 1: POPUP MANAGEMENT
         ──────────────────────────────────────────────────────────────────────── */}
      {activeTab === "popups" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 font-medium">
              Configured Promotional Popups
            </p>
            <button
              onClick={openCreatePopup}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 transition shadow-sm"
            >
              <FiPlus size={15} /> Add New Popup
            </button>
          </div>

          {/* Popups Table */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 font-medium uppercase tracking-wider">
                  <tr>
                    <th className="py-3 px-4">Image</th>
                    <th className="py-3 px-4">Title & Details</th>
                    <th className="py-3 px-4">Trigger & Delay</th>
                    <th className="py-3 px-4">Size</th>
                    <th className="py-3 px-4">Date Range</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {popups.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-400">
                        No popups created yet. Click "Add New Popup" to create one.
                      </td>
                    </tr>
                  ) : (
                    popups.map((popup) => (
                      <tr key={popup.id} className="hover:bg-gray-50/70 transition">
                        <td className="py-3 px-4">
                          <img
                            src={getImageFullUrl(popup.image_url)}
                            alt={popup.title || "Popup"}
                            className="w-16 h-12 object-cover rounded-lg border border-gray-200 shadow-sm"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-bold text-gray-800">{popup.title || "Untitled Popup"}</div>
                          {popup.button_text && (
                            <div className="text-emerald-600 font-medium mt-0.5 flex items-center gap-1">
                              <span>Button: "{popup.button_text}"</span>
                              {popup.button_url && <FiExternalLink size={11} />}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-block bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium text-[11px]">
                            {popup.trigger_type.replace(/_/g, " ")}
                          </span>
                          {popup.display_delay > 0 && (
                            <div className="text-gray-500 mt-1 flex items-center gap-1">
                              <FiClock size={11} /> {popup.display_delay}s delay
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className="capitalize font-medium text-gray-700">{popup.popup_size}</span>
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-[11px]">
                          {popup.start_date || popup.end_date ? (
                            <div>
                              <div>From: {popup.start_date ? new Date(popup.start_date).toLocaleDateString() : "Always"}</div>
                              <div>To: {popup.end_date ? new Date(popup.end_date).toLocaleDateString() : "Always"}</div>
                            </div>
                          ) : (
                            <span className="text-gray-400">Always Active</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleTogglePopup(popup.id)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1 border transition ${
                              popup.is_active
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                            }`}
                          >
                            {popup.is_active ? <FiCheck size={12} /> : <FiX size={12} />}
                            {popup.is_active ? "Active" : "Disabled"}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setPreviewItem({ type: "popup", data: popup })}
                              title="Preview"
                              className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
                            >
                              <FiEye size={15} />
                            </button>
                            <button
                              onClick={() => openEditPopup(popup)}
                              title="Edit"
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            >
                              <FiEdit2 size={15} />
                            </button>
                            <button
                              onClick={() => handleDeletePopup(popup.id)}
                              title="Delete"
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                            >
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────────────────
          TAB 2: BANNER MANAGEMENT
         ──────────────────────────────────────────────────────────────────────── */}
      {activeTab === "banners" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 font-medium">
              Homepage Hero Swiper Banners (Sorted by Display Order)
            </p>
            <button
              onClick={openCreateBanner}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 transition shadow-sm"
            >
              <FiPlus size={15} /> Add New Banner
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {banners.length === 0 ? (
              <div className="col-span-full bg-white p-8 rounded-xl border border-gray-100 text-center text-gray-400">
                No hero banners configured. Click "Add New Banner" to add your first banner.
              </div>
            ) : (
              banners.map((banner) => (
                <div
                  key={banner.id}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition"
                >
                  {/* Banner Image Preview */}
                  <div className="relative aspect-[16/7] bg-gray-900 overflow-hidden group">
                    <img
                      src={getImageFullUrl(banner.desktop_image_url)}
                      alt={banner.title || "Banner"}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                    />
                    <div className="absolute top-2.5 left-2.5 bg-black/60 backdrop-blur-sm text-white px-2 py-0.5 rounded text-[10px] font-bold">
                      Order: #{banner.display_order}
                    </div>
                    <div className="absolute top-2.5 right-2.5">
                      <button
                        onClick={() => handleToggleBanner(banner.id)}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold shadow ${
                          banner.is_active ? "bg-emerald-500 text-white" : "bg-red-500 text-white"
                        }`}
                      >
                        {banner.is_active ? "Active" : "Disabled"}
                      </button>
                    </div>
                  </div>

                  {/* Banner Card Body */}
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="font-bold text-gray-800 text-sm">{banner.title || "Untitled Banner"}</h3>
                      {banner.redirect_url && (
                        <a
                          href={banner.redirect_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1 truncate"
                        >
                          <FiExternalLink size={12} className="shrink-0" />
                          <span className="truncate">{banner.redirect_url}</span>
                        </a>
                      )}
                      {(banner.start_date || banner.end_date) && (
                        <div className="text-[11px] text-gray-400 mt-2 flex items-center gap-1">
                          <FiCalendar size={12} />
                          <span>
                            {banner.start_date ? new Date(banner.start_date).toLocaleDateString() : "Always"} -{" "}
                            {banner.end_date ? new Date(banner.end_date).toLocaleDateString() : "Always"}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between border-t border-gray-100 pt-3 mt-4">
                      <button
                        onClick={() => setPreviewItem({ type: "banner", data: banner })}
                        className="text-xs text-gray-600 hover:text-emerald-600 flex items-center gap-1"
                      >
                        <FiEye size={13} /> Preview
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditBanner(banner)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        >
                          <FiEdit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteBanner(banner.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────────────────
          TAB 3: ADS MANAGEMENT
         ──────────────────────────────────────────────────────────────────────── */}
      {activeTab === "ads" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            {/* Position filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-gray-500">Filter Position:</span>
              {[
                { label: "All Positions", value: "all" },
                { label: "Home Top", value: "home_top" },
                { label: "Home Middle", value: "home_middle" },
                { label: "Home Bottom", value: "home_bottom" },
                { label: "Game Page", value: "game_page" },
                { label: "Deposit Page", value: "deposit_page" },
                { label: "Withdrawal Page", value: "withdrawal_page" },
              ].map((pos) => (
                <button
                  key={pos.value}
                  onClick={() => setAdPositionFilter(pos.value)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                    adPositionFilter === pos.value
                      ? "bg-gray-800 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                  }`}
                >
                  {pos.label}
                </button>
              ))}
            </div>

            <button
              onClick={openCreateAd}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 transition shadow-sm self-start sm:self-auto"
            >
              <FiPlus size={15} /> Add New Ad
            </button>
          </div>

          {/* Ads List Table */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 font-medium uppercase tracking-wider">
                  <tr>
                    <th className="py-3 px-4">Banner / Creative</th>
                    <th className="py-3 px-4">Title & Description</th>
                    <th className="py-3 px-4">Target Position</th>
                    <th className="py-3 px-4">Priority</th>
                    <th className="py-3 px-4">Button Action</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredAds.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-400">
                        No promotional ads found for this position.
                      </td>
                    </tr>
                  ) : (
                    filteredAds.map((ad) => (
                      <tr key={ad.id} className="hover:bg-gray-50/70 transition">
                        <td className="py-3 px-4">
                          <img
                            src={getImageFullUrl(ad.image_url)}
                            alt={ad.title || "Ad"}
                            className="w-20 h-10 object-cover rounded-lg border border-gray-200 shadow-sm"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-bold text-gray-800">{ad.title || "Untitled Ad"}</div>
                          {ad.description && <div className="text-gray-500 text-[11px] truncate max-w-xs">{ad.description}</div>}
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-block bg-purple-50 text-purple-700 px-2 py-0.5 rounded font-semibold text-[11px]">
                            {ad.position.replace(/_/g, " ").toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-semibold text-gray-700">
                          #{ad.priority}
                        </td>
                        <td className="py-3 px-4">
                          {ad.button_text ? (
                            <a
                              href={ad.button_url || "#"}
                              target="_blank"
                              rel="noreferrer"
                              className="text-emerald-600 font-medium hover:underline flex items-center gap-1"
                            >
                              <span>{ad.button_text}</span>
                              <FiExternalLink size={11} />
                            </a>
                          ) : (
                            <span className="text-gray-400">No button</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleToggleAd(ad.id)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1 border transition ${
                              ad.is_active
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                            }`}
                          >
                            {ad.is_active ? <FiCheck size={12} /> : <FiX size={12} />}
                            {ad.is_active ? "Active" : "Disabled"}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setPreviewItem({ type: "ad", data: ad })}
                              title="Preview"
                              className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
                            >
                              <FiEye size={15} />
                            </button>
                            <button
                              onClick={() => openEditAd(ad)}
                              title="Edit"
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            >
                              <FiEdit2 size={15} />
                            </button>
                            <button
                              onClick={() => handleDeleteAd(ad.id)}
                              title="Delete"
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                            >
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────────────────
          MODAL 1: CREATE / EDIT POPUP
         ──────────────────────────────────────────────────────────────────────── */}
      {isPopupModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl border border-gray-100 overflow-hidden my-8">
            <div className="flex items-center justify-between bg-gray-50 px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <FiTv className="text-emerald-600" />
                {editingPopup ? "Edit Popup" : "Create New Popup"}
              </h2>
              <button
                onClick={() => setIsPopupModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleSavePopup} className="p-6 space-y-4 text-xs max-h-[80vh] overflow-y-auto">
              {/* Image Upload / URL */}
              <div>
                <label className="block text-gray-700 font-semibold mb-1">
                  Popup Banner Image <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 hover:border-emerald-500 rounded-xl p-4 cursor-pointer bg-gray-50/50 transition">
                    <FiUploadCloud size={24} className="text-gray-400 mb-1" />
                    <span className="text-[11px] font-medium text-gray-600">
                      {popupForm.imageFile ? popupForm.imageFile.name : "Click to upload image file"}
                    </span>
                    <span className="text-[10px] text-gray-400 mt-0.5">PNG, JPG, WEBP up to 10MB</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          setPopupForm({ ...popupForm, imageFile: e.target.files[0] });
                        }
                      }}
                      className="hidden"
                    />
                  </label>
                </div>

                {/* Direct image url fallback or thumbnail */}
                {(popupForm.imageFile || popupForm.image_url) && (
                  <div className="mt-2 relative rounded-lg overflow-hidden border border-gray-200 max-h-32 flex justify-center bg-gray-900">
                    <img
                      src={
                        popupForm.imageFile
                          ? URL.createObjectURL(popupForm.imageFile)
                          : getImageFullUrl(popupForm.image_url)
                      }
                      alt="Preview"
                      className="h-32 object-contain"
                    />
                  </div>
                )}
              </div>

              {/* Title & Description */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Popup Title (Optional)</label>
                  <input
                    type="text"
                    value={popupForm.title}
                    onChange={(e) => setPopupForm({ ...popupForm, title: e.target.value })}
                    placeholder="e.g. Special Weekend Deposit Bonus 🎉"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Description / Subtext</label>
                  <textarea
                    rows={2}
                    value={popupForm.description}
                    onChange={(e) => setPopupForm({ ...popupForm, description: e.target.value })}
                    placeholder="Brief description shown below the popup image..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500 resize-none"
                  />
                </div>
              </div>

              {/* Button Text & URL */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Button Text</label>
                  <input
                    type="text"
                    value={popupForm.button_text}
                    onChange={(e) => setPopupForm({ ...popupForm, button_text: e.target.value })}
                    placeholder="e.g. Claim Bonus Now"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Button Redirect URL</label>
                  <input
                    type="text"
                    value={popupForm.button_url}
                    onChange={(e) => setPopupForm({ ...popupForm, button_url: e.target.value })}
                    placeholder="e.g. /deposit or https://..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Trigger Frequency & Delay */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Display Trigger</label>
                  <select
                    value={popupForm.trigger_type}
                    onChange={(e: any) => setPopupForm({ ...popupForm, trigger_type: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500 bg-white"
                  >
                    <option value="on_load">On Page Load</option>
                    <option value="after_delay">After a Few Seconds</option>
                    <option value="once_per_session">Once Per Session</option>
                    <option value="every_time">Every Time Visited</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Display Delay (Seconds)</label>
                  <input
                    type="number"
                    min={0}
                    value={popupForm.display_delay}
                    onChange={(e) => setPopupForm({ ...popupForm, display_delay: parseInt(e.target.value, 10) || 0 })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Popup Size & Close Button */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Popup Modal Size</label>
                  <select
                    value={popupForm.popup_size}
                    onChange={(e: any) => setPopupForm({ ...popupForm, popup_size: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500 bg-white"
                  >
                    <option value="small">Small (320px)</option>
                    <option value="medium">Medium (400px - Recommended)</option>
                    <option value="large">Large (500px)</option>
                  </select>
                </div>

                <div className="flex flex-col justify-center">
                  <label className="text-gray-700 font-semibold mb-1">Show Close Button (×)</label>
                  <label className="flex items-center gap-2 cursor-pointer mt-1">
                    <input
                      type="checkbox"
                      checked={popupForm.show_close_button}
                      onChange={(e) => setPopupForm({ ...popupForm, show_close_button: e.target.checked })}
                      className="w-4 h-4 text-emerald-600 rounded"
                    />
                    <span className="text-gray-600 text-xs">Allow user to close popup</span>
                  </label>
                </div>
              </div>

              {/* Date Scheduling */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Start Date (Optional)</label>
                  <input
                    type="datetime-local"
                    value={popupForm.start_date}
                    onChange={(e) => setPopupForm({ ...popupForm, start_date: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">End Date (Optional)</label>
                  <input
                    type="datetime-local"
                    value={popupForm.end_date}
                    onChange={(e) => setPopupForm({ ...popupForm, end_date: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500 bg-white"
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="popupActive"
                  checked={popupForm.is_active}
                  onChange={(e) => setPopupForm({ ...popupForm, is_active: e.target.checked })}
                  className="w-4 h-4 text-emerald-600 rounded"
                />
                <label htmlFor="popupActive" className="text-gray-700 font-semibold cursor-pointer">
                  Activate popup immediately
                </label>
              </div>

              {/* Form Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsPopupModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold shadow-sm"
                >
                  {editingPopup ? "Save Changes" : "Create Popup"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────────────────
          MODAL 2: CREATE / EDIT BANNER
         ──────────────────────────────────────────────────────────────────────── */}
      {isBannerModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl border border-gray-100 overflow-hidden my-8">
            <div className="flex items-center justify-between bg-gray-50 px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <FiImage className="text-emerald-600" />
                {editingBanner ? "Edit Hero Banner" : "Add New Hero Banner"}
              </h2>
              <button
                onClick={() => setIsBannerModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveBanner} className="p-6 space-y-4 text-xs max-h-[80vh] overflow-y-auto">
              {/* Desktop Banner Upload */}
              <div>
                <label className="block text-gray-700 font-semibold mb-1">
                  Desktop Banner Image <span className="text-red-500">*</span>
                </label>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 hover:border-emerald-500 rounded-xl p-4 cursor-pointer bg-gray-50/50 transition">
                  <FiUploadCloud size={24} className="text-gray-400 mb-1" />
                  <span className="text-[11px] font-medium text-gray-600">
                    {bannerForm.desktopFile ? bannerForm.desktopFile.name : "Upload Desktop Banner Image (e.g. 1920x600 or 1200x450)"}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setBannerForm({ ...bannerForm, desktopFile: e.target.files[0] });
                      }
                    }}
                    className="hidden"
                  />
                </label>
                {(bannerForm.desktopFile || bannerForm.desktop_image_url) && (
                  <div className="mt-2 relative rounded-lg overflow-hidden border border-gray-200 max-h-24 bg-gray-900 flex justify-center">
                    <img
                      src={
                        bannerForm.desktopFile
                          ? URL.createObjectURL(bannerForm.desktopFile)
                          : getImageFullUrl(bannerForm.desktop_image_url)
                      }
                      alt="Desktop Preview"
                      className="h-24 object-contain"
                    />
                  </div>
                )}
              </div>

              {/* Mobile Banner Upload */}
              <div>
                <label className="block text-gray-700 font-semibold mb-1">
                  Mobile Banner Image (Optional, defaults to Desktop)
                </label>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 hover:border-emerald-500 rounded-xl p-3 cursor-pointer bg-gray-50/50 transition">
                  <FiUploadCloud size={20} className="text-gray-400 mb-1" />
                  <span className="text-[11px] font-medium text-gray-600">
                    {bannerForm.mobileFile ? bannerForm.mobileFile.name : "Upload Mobile Optimized Image"}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setBannerForm({ ...bannerForm, mobileFile: e.target.files[0] });
                      }
                    }}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Title & Redirect URL */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Banner Title</label>
                  <input
                    type="text"
                    value={bannerForm.title}
                    onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
                    placeholder="e.g. VIP Rewards Promo"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Redirect Link / URL</label>
                  <input
                    type="text"
                    value={bannerForm.redirect_url}
                    onChange={(e) => setBannerForm({ ...bannerForm, redirect_url: e.target.value })}
                    placeholder="e.g. /promotions or https://..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Display Order */}
              <div>
                <label className="block text-gray-700 font-semibold mb-1">Display Order (1, 2, 3...)</label>
                <input
                  type="number"
                  min={0}
                  value={bannerForm.display_order}
                  onChange={(e) => setBannerForm({ ...bannerForm, display_order: parseInt(e.target.value, 10) || 0 })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Start Date (Optional)</label>
                  <input
                    type="datetime-local"
                    value={bannerForm.start_date}
                    onChange={(e) => setBannerForm({ ...bannerForm, start_date: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">End Date (Optional)</label>
                  <input
                    type="datetime-local"
                    value={bannerForm.end_date}
                    onChange={(e) => setBannerForm({ ...bannerForm, end_date: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500 bg-white"
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="bannerActive"
                  checked={bannerForm.is_active}
                  onChange={(e) => setBannerForm({ ...bannerForm, is_active: e.target.checked })}
                  className="w-4 h-4 text-emerald-600 rounded"
                />
                <label htmlFor="bannerActive" className="text-gray-700 font-semibold cursor-pointer">
                  Activate banner immediately
                </label>
              </div>

              {/* Form Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsBannerModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold shadow-sm"
                >
                  {editingBanner ? "Save Changes" : "Create Banner"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────────────────
          MODAL 3: CREATE / EDIT AD
         ──────────────────────────────────────────────────────────────────────── */}
      {isAdModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl border border-gray-100 overflow-hidden my-8">
            <div className="flex items-center justify-between bg-gray-50 px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <FiLayers className="text-emerald-600" />
                {editingAd ? "Edit Advertisement" : "Create Custom Advertisement"}
              </h2>
              <button
                onClick={() => setIsAdModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveAd} className="p-6 space-y-4 text-xs max-h-[80vh] overflow-y-auto">
              {/* Image Upload */}
              <div>
                <label className="block text-gray-700 font-semibold mb-1">
                  Ad Creative Banner <span className="text-red-500">*</span>
                </label>
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 hover:border-emerald-500 rounded-xl p-4 cursor-pointer bg-gray-50/50 transition">
                  <FiUploadCloud size={24} className="text-gray-400 mb-1" />
                  <span className="text-[11px] font-medium text-gray-600">
                    {adForm.imageFile ? adForm.imageFile.name : "Upload Ad Image / Banner"}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setAdForm({ ...adForm, imageFile: e.target.files[0] });
                      }
                    }}
                    className="hidden"
                  />
                </label>
                {(adForm.imageFile || adForm.image_url) && (
                  <div className="mt-2 relative rounded-lg overflow-hidden border border-gray-200 max-h-28 bg-gray-900 flex justify-center">
                    <img
                      src={
                        adForm.imageFile
                          ? URL.createObjectURL(adForm.imageFile)
                          : getImageFullUrl(adForm.image_url)
                      }
                      alt="Ad Preview"
                      className="h-28 object-contain"
                    />
                  </div>
                )}
              </div>

              {/* Target Position & Priority */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Display Position</label>
                  <select
                    value={adForm.position}
                    onChange={(e: any) => setAdForm({ ...adForm, position: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500 bg-white font-medium"
                  >
                    <option value="home_top">Home Page Top</option>
                    <option value="home_middle">Home Page Middle</option>
                    <option value="home_bottom">Home Page Bottom</option>
                    <option value="game_page">Game Play Page</option>
                    <option value="deposit_page">Deposit Page</option>
                    <option value="withdrawal_page">Withdrawal Page</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Priority / Order</label>
                  <input
                    type="number"
                    min={0}
                    value={adForm.priority}
                    onChange={(e) => setAdForm({ ...adForm, priority: parseInt(e.target.value, 10) || 0 })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Title & Description */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Ad Title (Optional)</label>
                  <input
                    type="text"
                    value={adForm.title}
                    onChange={(e) => setAdForm({ ...adForm, title: e.target.value })}
                    placeholder="e.g. 500% Welcome Cashback"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Ad Subtext / Description</label>
                  <input
                    type="text"
                    value={adForm.description}
                    onChange={(e) => setAdForm({ ...adForm, description: e.target.value })}
                    placeholder="e.g. Deposit ₹500 and get instant VIP spins!"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Button Text & URL */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Button Text</label>
                  <input
                    type="text"
                    value={adForm.button_text}
                    onChange={(e) => setAdForm({ ...adForm, button_text: e.target.value })}
                    placeholder="e.g. Play Now"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Button Action URL</label>
                  <input
                    type="text"
                    value={adForm.button_url}
                    onChange={(e) => setAdForm({ ...adForm, button_url: e.target.value })}
                    placeholder="e.g. /category/Slots or /deposit"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Start Date (Optional)</label>
                  <input
                    type="datetime-local"
                    value={adForm.start_date}
                    onChange={(e) => setAdForm({ ...adForm, start_date: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">End Date (Optional)</label>
                  <input
                    type="datetime-local"
                    value={adForm.end_date}
                    onChange={(e) => setAdForm({ ...adForm, end_date: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-emerald-500 bg-white"
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="adActive"
                  checked={adForm.is_active}
                  onChange={(e) => setAdForm({ ...adForm, is_active: e.target.checked })}
                  className="w-4 h-4 text-emerald-600 rounded"
                />
                <label htmlFor="adActive" className="text-gray-700 font-semibold cursor-pointer">
                  Activate advertisement immediately
                </label>
              </div>

              {/* Form Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsAdModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold shadow-sm"
                >
                  {editingAd ? "Save Changes" : "Create Ad"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────────────────
          LIVE PREVIEW MODAL (Mimics user site appearance)
         ──────────────────────────────────────────────────────────────────────── */}
      {previewItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
          <div className="relative w-full max-w-md bg-[#011e1a] border border-[#075A51] rounded-2xl p-6 text-white shadow-2xl">
            <button
              onClick={() => setPreviewItem(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-white p-1 rounded-full bg-black/40"
            >
              <FiX size={18} />
            </button>

            <div className="text-center mb-4">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#ffd065] bg-[#ffd065]/10 px-2.5 py-1 rounded-full border border-[#ffd065]/30">
                Live User Site Preview
              </span>
            </div>

            {previewItem.type === "popup" && (
              <div className="space-y-4">
                <div className="rounded-xl overflow-hidden border border-[#075A51]/50 shadow-lg">
                  <img
                    src={getImageFullUrl(previewItem.data.image_url)}
                    alt={previewItem.data.title || "Popup"}
                    className="w-full h-auto object-cover block"
                  />
                </div>

                {previewItem.data.title && (
                  <h3 className="font-bold text-center text-white text-base mt-2">
                    {previewItem.data.title}
                  </h3>
                )}

                {previewItem.data.description && (
                  <p className="text-xs text-[#50CBBD] text-center leading-relaxed">
                    {previewItem.data.description}
                  </p>
                )}

                {previewItem.data.button_text && (
                  <button className="w-full py-2.5 rounded-lg bg-[#ffd065] hover:brightness-105 text-[#013b35] font-bold text-sm shadow-md transition">
                    {previewItem.data.button_text}
                  </button>
                )}
              </div>
            )}

            {previewItem.type === "banner" && (
              <div className="space-y-3">
                <div className="rounded-xl overflow-hidden border border-[#075A51] shadow-lg aspect-[16/7]">
                  <img
                    src={getImageFullUrl(previewItem.data.desktop_image_url)}
                    alt={previewItem.data.title || "Banner"}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center text-xs text-[#50CBBD]">
                  {previewItem.data.title || "Hero Swiper Banner"}
                </div>
              </div>
            )}

            {previewItem.type === "ad" && (
              <div className="space-y-3">
                <div className="rounded-xl overflow-hidden border border-[#075A51] shadow-lg">
                  <img
                    src={getImageFullUrl(previewItem.data.image_url)}
                    alt={previewItem.data.title || "Ad"}
                    className="w-full h-auto object-cover"
                  />
                </div>
                {previewItem.data.title && (
                  <div className="font-bold text-white text-sm">{previewItem.data.title}</div>
                )}
                {previewItem.data.description && (
                  <div className="text-xs text-[#50CBBD]">{previewItem.data.description}</div>
                )}
                {previewItem.data.button_text && (
                  <button className="w-full py-2 rounded-lg bg-gradient-to-r from-emerald-400 to-emerald-600 text-black font-bold text-xs">
                    {previewItem.data.button_text}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionsManagement;
